import { styled } from 'linaria/lib/react';

import Marquee from 'react-fast-marquee';
import Grain from '../../UI/Grain';
import StyledButton from '../../UI/Button/styled';
import { COLORS, FONTS } from '../../../styles/constants';
import { BUTTON_STYLES } from '../../UI/Button/constants';

export const StyledGrain = styled(Grain)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

export const StyledInfoContainer = styled.div<{
  hasWhiteAvatar: boolean;
  hasPaddingBottom: boolean;
  removePaddingLeftRight?: boolean;
  gradientStart?: string | null;
  gradientEnd?: string | null;
  isDark?: boolean;
  showGradient?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 64px ${({ removePaddingLeftRight }) => (removePaddingLeftRight ? 0 : 24)}px
    ${({ hasPaddingBottom }) => (hasPaddingBottom ? '48px' : 0)};
  background: ${({ showGradient, gradientStart, gradientEnd, isDark }) =>
    showGradient
      ? `linear-gradient(${gradientStart ?? (isDark ? COLORS.black[60] : COLORS.white[60])} 5%, ${
          gradientEnd ?? (isDark ? COLORS.black[60] : COLORS.white[60])
        } 100%)`
      : 'none'};

  .avatar {
    background-color: ${({ hasWhiteAvatar }) => (hasWhiteAvatar ? COLORS.white[10] : COLORS.brown.light[100])};
  }

  &::before {
    background: ${({ showGradient, gradientStart, gradientEnd, isDark }) =>
      showGradient
        ? `linear-gradient(${gradientStart ?? (isDark ? COLORS.black[60] : COLORS.white[60])} 5%, ${
            gradientEnd ?? (isDark ? COLORS.black[60] : COLORS.white[60])
          } 100%)`
        : 'none'};
  }
`;

export const StyledFullName = styled.span`
  font: 400 28px/120% ${FONTS.primary};
  padding: 16px 0 4px 0;
  text-align: center;
  word-break: break-word;
`;

export const StyledUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  font: 14px/140% ${FONTS.secondary};
  text-align: center;
  gap: 8px;
  opacity: 0;
  animation: fadeIn 0.3s cubic-bezier(0.33, 0, 0.3, 1) forwards;
  animation-delay: 0.46s;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const StyledContainer = styled.div<{
  isExpanded?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
  text-align: center;
  max-width: 342px;
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.04);
  overflow: ${({ isExpanded }) => (isExpanded ? 'auto' : 'hidden')};
  cursor: pointer;
  transition: max-height 0.3s ease-in-out;
`;

export const StyledUserInfoMarquee = styled(Marquee)`
  font-size: 14px;
  font-family: inherit;
  font-weight: inherit;
  line-height: inherit;
  max-width: 342px;
  mask-image: linear-gradient(90deg, transparent 0%, white 15%, white 85%, transparent 100%);

  /* iOS-specific fixes */
  @supports (-webkit-touch-callout: none) {
    font-size: 14px !important;
    transform: scale(1);
    -webkit-text-size-adjust: 100%;
  }
`;

export const StyledExpandedText = styled.div`
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px;
  word-break: break-word;
  align-self: 'stretch';
`;

export const StyledFollowButtonContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  padding-top: 16px;
`;

export const StyledText = styled.span<{ shouldBreak?: boolean }>`
  word-break: ${({ shouldBreak }) => (shouldBreak ? 'break-word' : 'normal')};
  font-weight: 400;
`;

export const StyledLink = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-family: ${FONTS.secondary};
  font-size: 14px;
  font-weight: 600;
  margin: 0 auto;
  text-decoration: none;
`;

export const StyledSection = styled.section<{
  fontColor: string;
  fixedHeight: boolean;
  gradientStart?: string | null;
  gradientEnd?: string | null;
  isDark: boolean;
}>`
  color: ${({ fontColor }) => fontColor};
  min-height: auto;
  position: relative;
  background: ${({ gradientStart, isDark }) => gradientStart ?? (isDark ? COLORS.black[100] : COLORS.white[100])};
  transition: 0.5s all ease-in-out;

  &::before {
    background: ${({ gradientEnd }) =>
      `linear-gradient(${COLORS.transparent} 5%, ${gradientEnd ?? COLORS.black[60]} 100%)`};
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    opacity: ${({ gradientStart }) => (gradientStart ? '1' : '0')};
  }
`;

export const StyledIcon = styled.span`
  display: inline-block;
  padding: 0 4px;
`;

export const StyledVerifiedIcon = styled.span`
  display: inline-flex;
  padding: 0 4px 0 6px;
  vertical-align: middle;
`;

export const StyledSubtitle = styled.span`
  font: 600 14px/140% ${FONTS.secondary};
`;

export const StyledVerificationWrap = styled.div`
  padding-top: 4px;
`;

export const StyledWrap = styled.span`
  flex-shrink: 0;
`;

export const StyledMessageButton = styled(StyledButton)`
  border: 1px solid ${({ isDark }) => (isDark ? COLORS.white[30] : COLORS.brown.light[100])};
  box-sizing: border-box;

  &:disabled {
    background: ${({ isDark, buttonType }) => BUTTON_STYLES.background[buttonType].default[isDark ? 'dark' : 'light']};
    color: ${({ isDark, buttonType }) => BUTTON_STYLES.color[buttonType].default[isDark ? 'dark' : 'light']};
    cursor: default;
  }
`;

export const StyledTabsContainer = styled.div`
  display: flex;
  padding: 24px 40px 32px 40px;
  justify-content: center;
  align-items: flex-start;
  gap: 48px;
  opacity: 0;
  animation: fadeIn 0.3s cubic-bezier(0.33, 0, 0.3, 1) forwards;
  animation-delay: 0.46s;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const StyledTab = styled.div<{ isDark?: boolean }>`
  color: ${({ isDark = true }) => {
    return isDark ? COLORS.white[100] : COLORS.brown.dark[100];
  }};
  cursor: pointer;
  font: 600 14px/140% ${FONTS.secondary};
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  align-items: center;
  gap: 2px;

  /* Control SVG fill color based on active state */
  svg circle {
    fill: ${({ isDark = true }) => {
      return isDark ? COLORS.white[100] : COLORS.brown.dark[100];
    }};
  }
`;
